import ActionInput from 'automation-ui-action-features/action-inputs/records/ActionInput';
import JsonSchema from 'automation-ui-action-features/action-inputs/records/JsonSchema';
import QueryParam from 'automation-ui-action-features/action-inputs/records/QueryParam';
import OutputFieldKeys from 'automation-ui-action-features/action-outputs/records/OutputFieldKeys';
import * as RequestTypes from 'automation-ui-action-features/actions/webhook/constants/RequestTypes';
import * as WebhookActionMetadataKeys from 'automation-ui-action-features/actions/webhook/constants/WebhookActionMetadataKeys';
import AuthSettings from 'automation-ui-action-features/actions/webhook/records/AuthSettings';
import { formatQueryParamVariables } from 'automation-ui-action-features/actions/webhook/utils/metadataUtils';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
import OutputTypeDefinition from 'automation-ui-flow-core/fields/OutputTypeDefinition';
import { EXECUTION_STATE_OUTPUT, SERVER_RESPONSE, STATUS_CODE } from 'automation-ui-formula-editor/formula-editor/action-outputs/constants/HubSpotDefinedOutputNames';
import { Map as ImmutableMap, List, OrderedMap, Record } from 'immutable';
import getIn from 'immutable-less/transmute/getIn';

// Exported for unit testing purposes

export default class WebhookActionMetadata extends Record({
  [WebhookActionMetadataKeys.ACTION_TYPE]: ActionTypes.WEBHOOK,
  [WebhookActionMetadataKeys.ACTION_TYPE_ID]: null,
  [WebhookActionMetadataKeys.URL]: null,
  [WebhookActionMetadataKeys.APP_ID]: null,
  [WebhookActionMetadataKeys.AUTH_SETTINGS]: null,
  [WebhookActionMetadataKeys.METHOD]: null,
  [WebhookActionMetadataKeys.OUTPUT_FIELDS]: null,
  [WebhookActionMetadataKeys.QUERY_PARAMS]: null,
  [WebhookActionMetadataKeys.JSON_SCHEMA]: null
}, 'WebhookActionMetadata') {
  constructor(props) {
    let propMap = ImmutableMap(props || {});

    // fetch from props so it's typed as a plain object instead of an immutable
    const outputFields = getIn(['outputFields'], props);
    const queryParams = propMap.get(WebhookActionMetadataKeys.QUERY_PARAMS);
    const jsonSchema = propMap.get(WebhookActionMetadataKeys.JSON_SCHEMA);
    const authSettings = propMap.get(WebhookActionMetadataKeys.AUTH_SETTINGS);
    propMap = propMap.set(WebhookActionMetadataKeys.OUTPUT_FIELDS, outputFields ? List(outputFields.map(outputFieldDefinitionMap => {
      const outputFieldDefinition = outputFieldDefinitionMap[OutputFieldKeys.TYPE_DEFINITION];
      return ImmutableMap({
        [OutputFieldKeys.TYPE_DEFINITION]: new OutputTypeDefinition(
        // @ts-expect-error inner value of OutputFieldDefinition_1 is incorrectly typed as unknown
        outputFieldDefinition)
      });
    })) : null);
    propMap = propMap.set(WebhookActionMetadataKeys.QUERY_PARAMS, formatQueryParamVariables(queryParams));
    propMap = propMap.set(WebhookActionMetadataKeys.JSON_SCHEMA, jsonSchema ? new JsonSchema(jsonSchema) : null);
    propMap = propMap.set(WebhookActionMetadataKeys.AUTH_SETTINGS, authSettings ? new AuthSettings(authSettings) : null);
    super(propMap);
  }
  getMethod() {
    return this.get(WebhookActionMetadataKeys.METHOD);
  }
  isGetRequest() {
    return this.getMethod() === RequestTypes.GET;
  }
  isPostRequest() {
    return this.getMethod() === RequestTypes.POST;
  }
  getAppId() {
    return this.get(WebhookActionMetadataKeys.APP_ID);
  }
  getAuthSettings() {
    return this.get(WebhookActionMetadataKeys.AUTH_SETTINGS);
  }
  getURL() {
    return this.get(WebhookActionMetadataKeys.URL);
  }
  getActionTypeId() {
    return this.get(WebhookActionMetadataKeys.ACTION_TYPE_ID);
  }
  getOutputFields() {
    return this.get(WebhookActionMetadataKeys.OUTPUT_FIELDS);
  }
  setOutputFields(outputDefinitions) {
    return this.set(WebhookActionMetadataKeys.OUTPUT_FIELDS, outputDefinitions);
  }
  getQueryParams() {
    return this.get(WebhookActionMetadataKeys.QUERY_PARAMS);
  }
  getQueryParamVariables() {
    const queryParams = this.getQueryParams();
    return queryParams ? queryParams.getVariables() : null;
  }
  setQueryParamVariables(queryParams) {
    return this.set(WebhookActionMetadataKeys.QUERY_PARAMS, new QueryParam({
      [WebhookActionMetadataKeys.VARIABLES]: List(queryParams.map(queryParam => new ActionInput(queryParam)))
    }));
  }
  getSelectedValuesFromOutputFields() {
    let selectedValues = OrderedMap();
    const outputFields = this.getOutputFields();
    if (!outputFields) return selectedValues;
    outputFields.forEach(outputField => {
      let outputLabel = outputField.get(OutputFieldKeys.TYPE_DEFINITION).getLabel();
      const outputName = outputField.get(OutputFieldKeys.TYPE_DEFINITION).getName();

      // Developer Note: Special casing values without outputLabels,
      // these should only exist on QA or internal portals from when building out
      // the first passes of webhook outputs
      if (!outputLabel) {
        outputLabel = outputName;
      }
      // `hs_execution_state`, `hs_status_code`, and `hs_server_response` will be shown to customers
      // however they will not count against the maximum selected outputs.
      if (outputName && [EXECUTION_STATE_OUTPUT, STATUS_CODE, SERVER_RESPONSE].includes(outputName)) {
        selectedValues = selectedValues.set(outputName, outputName);
      } else {
        selectedValues = selectedValues.set(outputLabel, outputName);
      }
    });
    return selectedValues;
  }
  getJsonSchema() {
    return this.get(WebhookActionMetadataKeys.JSON_SCHEMA);
  }
  getJsonSchemaVariables() {
    /**
     * This method assumes that the JsonSchema is a list/map of JsonSchema leaves (as is the MVP for
     * Webhooks V2) and creates a flat list of variables.
     *
     * While Webhooks V2 BE expects an OBJECT schemaType, the ARRAY conditional of this method
     * is retained because schema variables before today (4.12.2022) was formatted to be an ARRAY and
     * we don't want old JsonSchemas to cause the UI to break.
     */
    const jsonSchema = this.getJsonSchema();
    if (!jsonSchema) return null;
    if (jsonSchema.getSchemaType() === 'ARRAY') {
      return jsonSchema.getSchemas().map(leaf => new ActionInput(leaf.getVariable())).toList();
    }
    return jsonSchema.getChildren().reduce((acc, value) => {
      return acc.push(new ActionInput(value.getVariable()));
    }, List());
  }
  getNamedQueryParamVariables() {
    const queryParam = this.getQueryParamVariables();
    return queryParam ? queryParam.filter(({
      name
    }) => !!name).toList() : null;
  }
  getNamedJsonSchemaVariables() {
    const variables = this.getJsonSchemaVariables();
    return variables ? variables.filter(({
      name
    }) => !!name).toList() : null;
  }
  getNamedFieldsForTestableOverrides() {
    const testableNamedInputs = this.isPostRequest() ? this.getNamedJsonSchemaVariables() : this.getNamedQueryParamVariables();
    return (testableNamedInputs || List()).filter(actionInput => !actionInput.isStaticActionInput()).toList();
  }
  getNamedStaticFieldsForTesting() {
    const namedInputs = this.isPostRequest() ? this.getNamedJsonSchemaVariables() : this.getNamedQueryParamVariables();
    return (namedInputs || List()).filter(actionInput => actionInput.isStaticActionInput()).toList();
  }
}